<script setup lang="ts">
import { BsButton } from '@afo/bsf-core'
import { useWindowSize } from '@vueuse/core'
import { IButtonFilterTypes } from '@/domains/career/main/components/career-main-benefits/career-main-benefits.types'
import { BUTTONS_FILTERS } from '@/domains/career/main/components/career-main-benefits/career-main-benefits.consts'
import type { IProps, IButtonFilterType, IBenefitItems, IBenefitBreakpoints } from '@/domains/career/main/components/career-main-benefits/career-main-benefits.types'
import CareerMainBenefit from '@/domains/career/main/components/career-main-benefits/CareerMainBenefit/career-main-benefit.vue'

const { benefits } = defineProps<IProps>()

const { t } = useI18n()
const { isMobile, isTablet, isDesktop } = useBSBreakpoints()
const { width: currentWindowWidth } = useWindowSize()

const activeFilter = ref<IButtonFilterType>(IButtonFilterTypes.ALL as unknown as IButtonFilterType)
const showAllBenefits = ref(false)

const breakpointLevels: IBenefitBreakpoints = {
  mobile: 3,
  tablet: 6,
  desktop: 8,
  all: benefits.flatMap(b => b.benefits).length,
}

const benefitItems = computed<IBenefitItems[]>(() => {
  const items = getCurrentItems()
  const breakpoint = getCurrentBreakpoint()
  return sliceBenefits(items, breakpoint)
})

const showAllBenefitsButton = computed(() => {
  const items = getCurrentItems()
  const currentBreakpoint = getCurrentBreakpoint()
  return items.length > currentBreakpoint
})

const displayAllBenefitsButtonText = computed(() => {
  return showAllBenefits.value ? t('career.benefits.button.hideAll') : t('career.benefits.button.showAll')
})

const getBenefitCategory = (benefitTitle: string) => {
  return benefits.find(b => b.benefits.some(b => b.title === benefitTitle))?.category || ''
}

const getCurrentBreakpoint = () => {
  if (showAllBenefits.value) {
    return breakpointLevels.all
  }

  if (isMobile.value && !isTablet.value) {
    return breakpointLevels.mobile
  }

  if (isTablet.value && !isDesktop.value) {
    return breakpointLevels.tablet
  }

  if (currentWindowWidth.value >= 1200 && currentWindowWidth.value < 1440) {
    return breakpointLevels.tablet
  }

  return breakpointLevels.desktop
}

const getCurrentItems = () => {
  if (activeFilter.value === IButtonFilterTypes.ALL as unknown as IButtonFilterType) {
    return benefits.flatMap(b => b.benefits)
  }

  const activeButton = BUTTONS_FILTERS.find(b => b.value === activeFilter.value)
  const items = benefits.find(b => b.category === t(activeButton?.text as string))

  return items?.benefits || []
}

const sliceBenefits = (benefits: IBenefitItems[], breakpoint: number): IBenefitItems[] => {
  return benefits.slice(0, breakpoint)
}

const setFilter = (value: IButtonFilterType) => {
  activeFilter.value = value
}

const handleArrowRight = () => {
  const currentIndex = BUTTONS_FILTERS.findIndex(b => b.value === activeFilter.value)
  const nextIndex = (currentIndex + 1) % BUTTONS_FILTERS.length
  setFilter(BUTTONS_FILTERS[nextIndex].value)
}

const handleArrowLeft = () => {
  const currentIndex = BUTTONS_FILTERS.findIndex(b => b.value === activeFilter.value)
  const prevIndex = currentIndex === 0 ? BUTTONS_FILTERS.length - 1 : currentIndex - 1
  setFilter(BUTTONS_FILTERS[prevIndex].value)
}
</script>

<template>
  <div class="career-main-benefits">
    <div class="career-main-benefits__container">
      <div class="career-main-benefits__title">
        {{ title }}
      </div>

      <div class="career-main-benefits__description">
        {{ $t('career.benefits.description') }}
      </div>

      <div
        class="career-main-benefits__filters"
        role="tablist"
        aria-label="Filtry benefitów"
      >
        <BsButton
          v-for="button in BUTTONS_FILTERS"
          :key="button.value"
          size="small"
          :variant="activeFilter === button.value ? 'primary' : 'secondary'"
          role="tab"
          :aria-selected="activeFilter === button.value"
          :aria-controls="`benefits-panel-${button.value}`"
          :tabindex="0"
          @click="setFilter(button.value)"
          @keydown.arrow-right="handleArrowRight"
          @keydown.arrow-left="handleArrowLeft"
        >
          {{ t(button.text) }}
        </BsButton>
      </div>

      <div
        :id="`benefits-panel-${activeFilter}`"
        class="career-main-benefits__benefits"
        role="tabpanel"
        :aria-labelledby="`tab-${activeFilter}`"
      >
        <Transition
          name="list"
          mode="out-in"
          appear
        >
          <div
            :key="activeFilter"
            class="career-main-benefits__benefits-container"
          >
            <CareerMainBenefit
              v-for="benefit in (benefitItems as IBenefitItems[])"
              :key="benefit.title"
              :benefit="benefit"
              :category="getBenefitCategory(benefit.title)"
            />
          </div>
        </Transition>
      </div>

      <div
        v-if="showAllBenefitsButton"
        class="career-main-benefits__button"
      >
        <BsButton
          variant="primary"
          :aria-expanded="showAllBenefits"
          :aria-controls="`benefits-panel-${activeFilter}`"
          @click="showAllBenefits = !showAllBenefits"
        >
          {{ displayAllBenefitsButtonText }}

          <img
            src="/icons/large_arrow_down_white.svg"
            alt="arrow-down"
            :class="['career-main-benefits__button-icon', showAllBenefits && 'career-main-benefits__button-icon--rotate']"
          >
        </BsButton>
      </div>
    </div>
  </div>
</template>

  <style lang="scss" scoped>
  .career-main-benefits {
    margin: 4.8rem 0;
    padding: 0 1.6rem;

    &__container {
      display: flex;
      flex-direction: column;
      align-items: center;
      align-self: stretch;
      padding: 6.4rem 1.6rem;
      background: linear-gradient(172deg, $gray-background 0.01%, $purple-50 100.01%);
      border-radius: 2.4rem;
    }

    &__title {
      @include h5;
      color: $text-500;
      text-align: center;
    }

    &__description {
      @include body-1-medium;
      padding: 3.2rem 0;
      color: $text-500;
      text-align: center;
    }

    &__filters {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 1.2rem;
    }

    &__benefits {
      padding: 6.4rem 0 0;
      transition: height 600ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    &__benefits-container {
      display: flex;
      flex-direction: column;
      gap: 1.6rem;
    }

    &__button {
      margin: 3.2rem 0 0;

      &-icon {
        margin-left: 0.8rem;

        &--rotate {
          transform: rotate(180deg);
        }
      }
    }

    @include atS {
      &__title {
        @include h5;
      }

      &__filters {
        gap: 2rem;
        width: 90%;
      }

      &__benefits-container {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 4.8rem;
      }

      &__button {
        margin: 4.8rem 0 0;
      }
    }

    @include atM {
      &__benefits-container {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    @media (min-width: 1440px) {
      &__benefits-container {
        grid-template-columns: repeat(4, 1fr);
      }
    }
  }

  //LIST
  .list-enter-active {
    transition: all 500ms ease-out;
  }

  .list-leave-active {
    transition: all 300ms ease-in;
  }

  .list-enter-from {
    transform: translateY(20px);
    opacity: 0;
  }

  .list-leave-to {
    transform: translateY(-10px);
    opacity: 0;
  }

  .list-enter-active .benefit-item {
    transition: all 500ms ease-out;
    transition-delay: 50ms;
  }

  .list-enter-from .benefit-item {
    transform: translateY(2rem);
    opacity: 0;
  }
  </style>

  <style lang="scss">
  .career-main-benefits {
    .bs-button.bs-button--small {
      height: 3.6rem;
      border-radius: 0.8rem;

      &:hover, &:focus, &:active {
        color: $white;
        background-color: rgba($brand-blue-500, 0.8);
        border: 1px solid rgba($brand-blue-500, 0.8);
        outline: none;
      }
    }

    .bs-button.bs-button--small.bs-button--primary {
      color: $white;
      background-color: $brand-blue-500;
    }

    .career-main-benefits__button {
      .bs-button.bs-button {
        border-radius: 2rem;
      }
    }
  }
  </style>

